import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ServisfyLogo } from "../../components/Common/ServisfyLogo";

// Public Partner Program page + application form (Slice A). No auth; posts to the public intake
// (/api/v1/public/partners/applications), which is non-enumerating + rate-limited. The response is a
// generic acknowledgement in every case, so this UI NEVER reveals whether an email already applied.
// A verification email follows (on staging it is redirected/suppressed by policy — never claimed as
// delivered to the applicant here).

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const PARTNER_TYPES = [
  { value: "referral", label: "Referral Partner", blurb: "Refer businesses to Servisfy and earn commission on qualifying subscriptions." },
  { value: "implementation", label: "Implementation Partner", blurb: "Help businesses onboard and succeed on Servisfy." },
  { value: "reseller", label: "Reseller", blurb: "Package and resell Servisfy under an approved agreement." },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Partners = () => {
  const [form, setForm] = useState({
    legal_name: "", contact_name: "", email: "", phone: "", country: "",
    partner_type: "referral", company_website: "", note: "",
  });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | submitting | submitted | error
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const formRef = useRef(null);
  // Selecting a partner-type card writes the SAME form.partner_type the dropdown binds to — one
  // source of truth, no parallel state. The dropdown stays in sync in both directions automatically.
  const selectType = (value) => {
    setForm((f) => ({ ...f, partner_type: value }));
    // Convenience scroll only; guarded and non-essential so keyboard focus and validation are unaffected.
    if (status !== "submitted" && formRef.current) {
      try { formRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (_e) { /* noop */ }
    }
  };

  const canSubmit =
    form.legal_name.trim() && form.contact_name.trim() &&
    EMAIL_RE.test(form.email.trim()) && consent && status !== "submitting";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!canSubmit) {
      setError("Please complete the required fields and accept the privacy notice.");
      return;
    }
    setStatus("submitting");
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/public/partners/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          legal_name: form.legal_name.trim(),
          contact_name: form.contact_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          country: form.country.trim() || null,
          partner_type: form.partner_type,
          company_website: form.company_website.trim() || null,
          note: form.note.trim() || null,
        }),
      });
      // Treat any 2xx as the generic acknowledgement. A 4xx (validation / rate limit) shows a safe,
      // non-enumerating message.
      if (res.ok) {
        setStatus("submitted");
      } else if (res.status === 429) {
        setStatus("error");
        setError("Too many requests right now. Please try again in a little while.");
      } else {
        setStatus("error");
        setError("We couldn't submit your application. Please check your details and try again.");
      }
    } catch (_e) {
      setStatus("error");
      setError("Network error. Please try again.");
    }
  };

  return (
    <div className="servisfy-brand min-h-screen text-foreground bg-background bg-gradient-to-br from-background via-secondary/40 to-accent/40" data-theme="light">
      <header className="max-w-4xl mx-auto px-6 pt-6">
        <div className="flex justify-end items-center mb-4">
          <Link to="/login" className="text-sm font-medium text-primary hover:underline underline-offset-2 whitespace-nowrap transition-colors">
            ← Back to Login
          </Link>
        </div>
        <div className="text-center mb-10" data-testid="partners-brand">
          <div className="flex items-center justify-center mb-4">
            <ServisfyLogo variant="full" size="xxl" className="mix-blend-multiply" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Servisfy <span className="text-brand-gradient">Partner Program</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Join the Servisfy Partner Program to grow with us. Applications are free, and approved
            partners receive a unique referral link and access to a secure partner portal.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 pb-12 pt-2">
        {/* Categories */}
        <div className="grid gap-4 sm:grid-cols-3 mb-10">
          {PARTNER_TYPES.map((t) => {
            const selected = form.partner_type === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => selectType(t.value)}
                aria-pressed={selected}
                aria-label={`Select ${t.label}`}
                data-testid={`partner-type-card-${t.value}`}
                className={`text-left rounded-xl border p-4 min-h-[88px] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  selected
                    ? "border-primary ring-2 ring-primary bg-accent/60 shadow-sm"
                    : "border-border bg-card hover:border-primary/50 hover:shadow-sm"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-foreground">{t.label}</span>
                  {selected && <span className="text-primary font-bold shrink-0" aria-hidden="true">✓</span>}
                </div>
                <div className="text-sm text-muted-foreground mt-1">{t.blurb}</div>
              </button>
            );
          })}
        </div>

        {status === "submitted" ? (
          <div className="rounded-xl border border-green-200 bg-green-50 p-6" data-testid="partner-submitted">
            <h2 className="text-xl font-semibold text-green-900 mb-2">Application received</h2>
            <p className="text-green-800">
              Thank you. If your application is eligible, we'll email you to verify your contact
              details and continue the review. Please check your inbox for a verification link.
            </p>
          </div>
        ) : (
          <form ref={formRef} onSubmit={submit} className="space-y-5" noValidate>
            <h2 className="text-2xl font-bold text-foreground">Apply to become a partner</h2>

            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Legal / company name" required>
                <input className="sf-input" value={form.legal_name} onChange={set("legal_name")}
                  aria-label="Legal or company name" />
              </Field>
              <Field label="Your name" required>
                <input className="sf-input" value={form.contact_name} onChange={set("contact_name")}
                  aria-label="Your name" />
              </Field>
              <Field label="Email" required>
                <input type="email" className="sf-input" value={form.email} onChange={set("email")}
                  aria-label="Email" />
              </Field>
              <Field label="Phone">
                <input className="sf-input" value={form.phone} onChange={set("phone")} aria-label="Phone" />
              </Field>
              <Field label="Country">
                <input className="sf-input" value={form.country} onChange={set("country")} aria-label="Country" />
              </Field>
              <Field label="Partner type" required>
                <select className="sf-input" value={form.partner_type} onChange={set("partner_type")}
                  aria-label="Partner type">
                  {PARTNER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Company website">
                <input className="sf-input" value={form.company_website} onChange={set("company_website")}
                  aria-label="Company website" />
              </Field>
            </div>

            <Field label="Anything else you'd like us to know?">
              <textarea rows={3} className="sf-input" value={form.note} onChange={set("note")}
                aria-label="Additional note" />
            </Field>

            <label className="flex items-start gap-2 text-sm text-muted-foreground">
              <input type="checkbox" className="mt-1" checked={consent}
                onChange={(e) => setConsent(e.target.checked)} aria-label="Accept privacy notice" />
              <span>
                I agree that Servisfy may process the information in this application to review my
                partner request, in accordance with the{" "}
                <Link to="/privacy-policy" className="text-primary underline">Privacy Policy</Link>.
              </span>
            </label>

            {error && <p className="text-sm text-red-600" role="alert">{error}</p>}

            <button type="submit" disabled={!canSubmit}
              className="bg-brand-gradient hover:brightness-110 disabled:opacity-50 text-white font-medium px-6 py-3 rounded-lg transition-all">
              {status === "submitting" ? "Submitting…" : "Submit application"}
            </button>
          </form>
        )}
      </main>

      {/* Minimal utility class so inputs look consistent without depending on a shared form kit. */}
      <style>{`.sf-input{width:100%;border:1px solid #d1d5db;border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.95rem;background:#fff;}
      .sf-input:focus{outline:2px solid #377272;outline-offset:1px;}`}</style>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-sm font-medium text-foreground mb-1">
      {label}{required && <span className="text-red-500"> *</span>}
    </span>
    {children}
  </label>
);

export default Partners;
